import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Grid, Card, CardContent, Button, TextField, MenuItem,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Checkbox, Paper, Chip, CircularProgress, Alert, Tooltip,
    Divider, InputAdornment, Radio, RadioGroup, FormControlLabel, FormControl
} from '@mui/material';
import {
    Phone, WhatsApp, Search, Refresh, Preview, InfoOutlined,
    Group, Work, Description
} from '@mui/icons-material';
import { api } from '../context/AuthContext';

const AICalling = () => {
    // Data states
    const [templates, setTemplates] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [credits, setCredits] = useState({ calling: { remaining: 0 }, whatsapp: { remaining: 0 } });
    const [loading, setLoading] = useState(false);
    const [fetchingCandidates, setFetchingCandidates] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [loadingInitial, setLoadingInitial] = useState(true);

    // Selection states
    const [commType, setCommType] = useState(''); // 'voice' or 'whatsapp'
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [selectedJob, setSelectedJob] = useState('');
    const [selectedCandidates, setSelectedCandidates] = useState([]);

    // UI states
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        setLoadingInitial(true);
        setError('');
        try {
            const jobsRes = await api.get('/admin/jobs');
            const jobsData = Array.isArray(jobsRes.data) ? jobsRes.data : (jobsRes.data.jobs || []);
            setJobs(jobsData);

            try {
                const statsRes = await api.get('/applications/comm-center/stats');
                if (statsRes.data) {
                    setCredits(statsRes.data);
                }
            } catch (statsErr) {
                console.warn('Failed to fetch stats:', statsErr);
                setCredits({ calling: { remaining: 0 }, whatsapp: { remaining: 0 } });
            }
        } catch (err) {
            setError('Failed to fetch initial data. Please ensure you are logged in as a recruiter.');
            console.error(err);
        } finally {
            setLoadingInitial(false);
        }
    };

    const fetchTemplates = async (type) => {
        setLoading(true);
        try {
            if (type === 'whatsapp') {
                const res = await api.get('/applications/comm-center/whatsapp-twilio-templates');
                setTemplates(res.data);
            } else {
                const res = await api.get(`/applications/comm-center/templates?type=${type}`);
                setTemplates(res.data.filter(t => t.ai_enabled));
            }
            setSelectedTemplate('');
        } catch (err) {
            setError(`Failed to fetch ${type} templates`);
        } finally {
            setLoading(false);
        }
    };

    const fetchCandidates = async (jobId) => {
        if (!jobId) return;
        setFetchingCandidates(true);
        setError('');
        try {
            const res = await api.get(`/applications/admin?jobId=${jobId}`);
            let candidatesData = [];
            if (Array.isArray(res.data)) {
                candidatesData = res.data;
            } else if (res.data.candidates) {
                candidatesData = res.data.candidates;
            } else if (res.data.applications) {
                candidatesData = res.data.applications;
            }
            setCandidates(candidatesData);
            setSelectedCandidates([]);
        } catch (err) {
            setError('Failed to fetch candidates');
            console.error(err);
        } finally {
            setFetchingCandidates(false);
        }
    };

    const handleCommTypeChange = (type) => {
        setCommType(type);
        fetchTemplates(type);
    };

    const handleJobChange = (jobId) => {
        setSelectedJob(jobId);
        fetchCandidates(jobId);
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedCandidates(filteredCandidates.map(c => c.candidate_id || c.id));
        } else {
            setSelectedCandidates([]);
        }
    };

    const handleSelectOne = (id) => {
        const selectedIndex = selectedCandidates.indexOf(id);
        let newSelected = [];
        if (selectedIndex === -1) {
            newSelected = [...selectedCandidates, id];
        } else {
            newSelected = selectedCandidates.filter(item => item !== id);
        }
        setSelectedCandidates(newSelected);
    };

    const getPreview = () => {
        const job = jobs.find(j => j.id === selectedJob);
        if (commType === 'whatsapp') {
            const template = templates.find(t => t.key === selectedTemplate);
            if (!template) return 'Select a template to see preview';
            return template.preview
                .replace(/\{\{candidateName\}\}/g, 'John Doe')
                .replace(/\{\{jobTitle\}\}/g, job ? job.title : 'Software Engineer')
                .replace(/\{\{companyName\}\}/g, job ? (job.company_name || 'ExiHire') : 'ExiHire')
                .replace(/\{\{interviewDate\}\}/g, '25th June 2026');
        }
        const template = templates.find(t => t.id === selectedTemplate);
        if (!template) return 'Select a template to see preview';
        return template.content
            .replace(/{{candidateName}}/g, 'John Doe')
            .replace(/{{jobTitle}}/g, job ? job.title : 'Software Engineer')
            .replace(/{{companyName}}/g, job ? (job.company_name || 'ExiHire') : 'ExiHire')
            .replace(/{{interviewDate}}/g, '25th June 2026')
            .replace(/{{interviewLink}}/g, '');
    };

    // ✅ Format status: removes underscores and capitalizes each word
    const formatStatus = (status) => {
        if (!status) return '-';
        return status
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const handleExecute = async () => {
        if (!commType || !selectedTemplate || !selectedJob || selectedCandidates.length === 0) {
            setError('Please complete all steps');
            return;
        }

        setActionLoading(true);
        setError('');
        setSuccess('');

        try {
            const endpoint = commType === 'voice'
                ? '/applications/comm-center/call-individual'
                : '/applications/comm-center/whatsapp-individual';

            const payload = commType === 'whatsapp'
                ? { candidateIds: selectedCandidates, templateKey: selectedTemplate, jobId: selectedJob }
                : { candidateIds: selectedCandidates, templateId: selectedTemplate, jobId: selectedJob };

            const res = await api.post(endpoint, payload);

            const results = res.data.results || [];
            const failedCount = results.filter(r => r.status === 'failed').length;
            const successCount = results.length - failedCount;

            if (successCount > 0) {
                setSuccess(`Successfully initiated ${commType === 'voice' ? 'AI Call' : 'WhatsApp'} for ${successCount} candidates.${failedCount > 0 ? ` Failed for ${failedCount} candidates.` : ''}`);
                setSelectedCandidates([]);
                try {
                    const statsRes = await api.get('/applications/comm-center/stats');
                    if (statsRes.data) setCredits(statsRes.data);
                } catch (e) {}
            } else if (failedCount > 0) {
                setError(`Failed to initiate ${commType === 'voice' ? 'AI Call' : 'WhatsApp'} for ${failedCount} candidates.`);
            }
        } catch (err) {
            setError(err.response?.data?.message || `Failed to initiate ${commType} communication`);
        } finally {
            setActionLoading(false);
        }
    };

    const filteredCandidates = candidates.filter(c => {
        const name = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
        const email = (c.email || '').toLowerCase();
        const phone = (c.phone || '').toLowerCase();
        const search = searchTerm.toLowerCase();
        const matchesSearch = name.includes(search) || email.includes(search) || phone.includes(search);

        // WhatsApp uses Twilio templates which are not tied to specific statuses
        if (!selectedTemplate || commType === 'whatsapp') return matchesSearch;

        const template = templates.find(t => t.id === selectedTemplate);
        if (!template) return matchesSearch;

        const type = template.template_type;
        const statusMap = {
            'Assessment Scheduled': ['ai_coding_scheduled'],
            'Technical Interview Scheduled': [
                'ai_technical_interview_scheduled',
                'technical_scheduled',
                'technical_l2_interview_scheduled',
                'TECHNICAL_L2_SCHEDULED'
            ],
            'HR Interview Scheduled': [
                'ai_hr_interview_scheduled',
                'hr_scheduled',
                'hr_l2_interview_scheduled'
            ],
            'Shortlisted': ['application_shortlisted'],
            'Selected': ['candidate_selected'],
            'Rejected': ['candidate_rejected']
        };

        const allowedStatuses = statusMap[type] || [];
        if (allowedStatuses.length === 0) return matchesSearch;
        return matchesSearch && allowedStatuses.includes(c.status);
    });

    const isStepComplete = (step) => {
        switch(step) {
            case 1: return !!commType;
            case 2: return !!selectedTemplate;
            case 3: return !!selectedJob;
            case 4: return selectedCandidates.length > 0;
            default: return false;
        }
    };

    const currentCredits = commType === 'voice'
        ? (credits.calling?.remaining || 0)
        : (credits.whatsapp?.remaining || 0);

    if (loadingInitial) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '80vh', gap: 2 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">Loading Recruiter Communication Hub...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ color: "#1E3A8A", fontWeight: "bold", fontSize: { xs: '1.5rem', md: '2rem' } }}>
                          Recruiter Communication Hub
                        </Typography>
                <Typography variant="body1" color="text.secondary">
                    Follow the steps below to contact candidates via AI Voice Call or WhatsApp.
                </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>{success}</Alert>}

            <Grid container spacing={3}>
                {/* Configuration Panel */}
                <Grid item xs={12} md={4}>
                    <Stack spacing={3}>
                        <Card sx={{ borderRadius: '16px', border: isStepComplete(1) ? '2px solid #10B981' : '1px solid #E2E8F0' }}>
                            <CardContent>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip label="1" size="small" color={isStepComplete(1) ? "success" : "primary"} /> Communication Type
                                </Typography>
                                <FormControl component="fieldset">
                                    <RadioGroup row value={commType} onChange={(e) => handleCommTypeChange(e.target.value)}>
                                        <FormControlLabel value="voice" control={<Radio />} label="AI Voice Call" />
                                        <FormControlLabel value="whatsapp" control={<Radio />} label="WhatsApp Message" />
                                    </RadioGroup>
                                </FormControl>
                            </CardContent>
                        </Card>

                        <Card sx={{ borderRadius: '16px', border: isStepComplete(2) ? '2px solid #10B981' : '1px solid #E2E8F0', opacity: !commType ? 0.6 : 1 }}>
                            <CardContent>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip label="2" size="small" color={isStepComplete(2) ? "success" : "primary"} /> Template Selection
                                </Typography>
                                {loading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
                                ) : (
                                    <>
                                        <TextField
                                            select
                                            fullWidth
                                            label={commType === 'voice' ? "Select Voice Template" : commType === 'whatsapp' ? "Select WhatsApp Template" : "Select Template"}
                                            value={selectedTemplate}
                                            onChange={(e) => setSelectedTemplate(e.target.value)}
                                            disabled={!commType}
                                            size="small"
                                        >
                                            {templates.map((t) => (
                                                <MenuItem key={commType === 'whatsapp' ? t.key : t.id} value={commType === 'whatsapp' ? t.key : t.id}>{t.name}</MenuItem>
                                            ))}
                                            {templates.length === 0 && <MenuItem disabled>No {commType} templates found</MenuItem>}
                                        </TextField>
                                        {commType === 'whatsapp' && (
                                            <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.disabled', lineHeight: 1.4 }}>
                                                These WhatsApp templates are pre-approved and managed in Twilio. Select a template to preview and send it to candidates.
                                            </Typography>
                                        )}
                                    </>
                                )}
                            </CardContent>
                        </Card>

                        <Card sx={{ borderRadius: '16px', border: isStepComplete(3) ? '2px solid #10B981' : '1px solid #E2E8F0' }}>
                            <CardContent>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip label="3" size="small" color={isStepComplete(3) ? "success" : "primary"} /> Job Selection
                                </Typography>
                                <TextField
                                    select
                                    fullWidth
                                    label="Select Job"
                                    value={selectedJob}
                                    onChange={(e) => handleJobChange(e.target.value)}
                                    size="small"
                                >
                                    {jobs.map((j) => (
                                        <MenuItem key={j.id} value={j.id}>
                                            {j.title} {j.job_id ? `(${j.job_id})` : ''}
                                        </MenuItem>
                                    ))}
                                    {jobs.length === 0 && <MenuItem disabled>No active jobs found</MenuItem>}
                                </TextField>
                            </CardContent>
                        </Card>

                        <Card sx={{ borderRadius: '16px', bgcolor: '#F8FAFC' }}>
                            <CardContent>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip label="5" size="small" color="primary" /> Template Preview
                                </Typography>
                                <Paper sx={{ p: 2, bgcolor: '#FFF', minHeight: 120, borderRadius: '12px', border: '1px solid #E2E8F0' }}>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary', fontSize: '0.85rem' }}>
                                        {getPreview()}
                                    </Typography>
                                </Paper>
                            </CardContent>
                        </Card>

                        <Card sx={{ borderRadius: '16px', border: '1px solid #E2E8F0', bgcolor: '#FFF', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                            <CardContent>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip label="6" size="small" color="primary" /> Summary
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">Communication Type:</Typography>
                                        <Typography variant="body2" fontWeight={700}>
                                            {commType === 'voice' ? 'AI Voice Call' : commType === 'whatsapp' ? 'WhatsApp Message' : '-'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">Selected Template:</Typography>
                                        <Typography variant="body2" fontWeight={700} sx={{ maxWidth: '150px', textAlign: 'right' }}>
                                            {templates.find(t => commType === 'whatsapp' ? t.key === selectedTemplate : t.id === selectedTemplate)?.name || '-'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">Selected Job:</Typography>
                                        <Typography variant="body2" fontWeight={700} sx={{ maxWidth: '150px', textAlign: 'right' }}>
                                            {jobs.find(j => j.id === selectedJob)?.title || '-'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">Selected Candidates Count:</Typography>
                                        <Typography variant="body2" fontWeight={700}>{selectedCandidates.length}</Typography>
                                    </Box>

                                    <Divider sx={{ my: 1 }} />

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">Current Balance:</Typography>
                                        <Typography variant="body2" fontWeight={700}
                                                    color={currentCredits < selectedCandidates.length ? 'error.main' : 'success.main'}
                                        >
                                            {currentCredits} Credits
                                        </Typography>
                                    </Box>

                                    {commType && credits[commType === 'voice' ? 'calling' : 'whatsapp']?.customRemaining > 0 && (
                                        <Box sx={{ bgcolor: '#EFF6FF', borderRadius: '8px', p: 1.5 }}>
                                            <Typography variant="caption" sx={{ color: '#1E3A8A', fontWeight: 700, display: 'block', mb: 0.5 }}>
                                                Credit Breakdown:
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Plan Credits: {commType === 'voice' ? (credits.calling?.planRemaining || 0) : (credits.whatsapp?.planRemaining || 0)}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#1E3A8A' }} display="block">
                                                Custom Plan Credits: {commType === 'voice' ? (credits.calling?.customRemaining || 0) : (credits.whatsapp?.customRemaining || 0)}
                                            </Typography>
                                        </Box>
                                    )}

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="subtitle2" fontWeight={700}>Credits Used:</Typography>
                                        <Typography variant="subtitle2" fontWeight={800} color="primary.main">
                                            {selectedCandidates.length}
                                        </Typography>
                                    </Box>

                                    {commType && currentCredits < selectedCandidates.length && (
                                        <Alert severity="error" size="small" sx={{ mt: 1, py: 0, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                                            Insufficient credits. Please contact support or purchase a custom plan.
                                        </Alert>
                                    )}
                                </Box>

                                <Button
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    sx={{
                                        mt: 3,
                                        fontWeight: 800,
                                        borderRadius: '12px',
                                        py: 1.5,
                                        boxShadow: '0 4px 12px rgba(30, 58, 138, 0.2)'
                                    }}
                                    startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : (commType === 'voice' ? <Phone /> : <WhatsApp />)}
                                    onClick={handleExecute}
                                    disabled={
                                        actionLoading ||
                                        !isStepComplete(1) ||
                                        !isStepComplete(2) ||
                                        !isStepComplete(3) ||
                                        !isStepComplete(4) ||
                                        currentCredits < selectedCandidates.length
                                    }
                                >
                                    {commType === 'voice' ? 'Start AI Calls' : 'Send WhatsApp Messages'}
                                </Button>
                                <Typography variant="caption" sx={{ mt: 2, display: 'block', color: 'text.secondary', textAlign: 'center', fontStyle: 'italic' }}>
                                    Credits are included with your subscription or custom plan.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Stack>
                </Grid>

                {/* Candidate Selection Panel */}
                <Grid item xs={12} md={8}>
                    <Card sx={{ borderRadius: '16px', height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip label="4" size="small" color={isStepComplete(4) ? "success" : "primary"} /> Select Candidates
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        size="small"
                                        placeholder="Search candidates..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<Refresh />}
                                        onClick={() => fetchCandidates(selectedJob)}
                                        disabled={!selectedJob || fetchingCandidates}
                                    >
                                        Refresh
                                    </Button>
                                </Box>
                            </Box>

                            <TableContainer sx={{ maxHeight: 600 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox" sx={{ bgcolor: '#F8FAFC' }}>
                                                <Checkbox
                                                    indeterminate={selectedCandidates.length > 0 && selectedCandidates.length < filteredCandidates.length}
                                                    checked={filteredCandidates.length > 0 && selectedCandidates.length === filteredCandidates.length}
                                                    onChange={handleSelectAll}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 700, bgcolor: '#F8FAFC' }}>Candidate</TableCell>
                                            <TableCell sx={{ fontWeight: 700, bgcolor: '#F8FAFC' }}>Email</TableCell>
                                            <TableCell sx={{ fontWeight: 700, bgcolor: '#F8FAFC' }}>Phone</TableCell>
                                            <TableCell sx={{ fontWeight: 700, bgcolor: '#F8FAFC' }}>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {fetchingCandidates ? (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ py: 10 }}>
                                                    <CircularProgress size={40} />
                                                    <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">Loading candidates...</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : filteredCandidates.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ py: 10 }}>
                                                    <Box sx={{ color: 'text.secondary', textAlign: 'center' }}>
                                                        <Group sx={{ fontSize: 48, mb: 2, opacity: 0.2 }} />
                                                        <Typography>
                                                            {selectedJob ? 'No candidates found for this job.' : 'Select a job to load candidates.'}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            filteredCandidates.map((c) => {
                                                const id = c.candidate_id || c.id;
                                                const isSelected = selectedCandidates.indexOf(id) !== -1;
                                                return (
                                                    <TableRow
                                                        key={c.id}
                                                        hover
                                                        selected={isSelected}
                                                        onClick={() => handleSelectOne(id)}
                                                        sx={{ cursor: 'pointer' }}
                                                    >
                                                        <TableCell padding="checkbox">
                                                            <Checkbox checked={isSelected} />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" fontWeight={600}>{c.first_name} {c.last_name}</Typography>
                                                            <Typography variant="caption" color="text.secondary">{c.job_title}</Typography>
                                                        </TableCell>
                                                        <TableCell>{c.email}</TableCell>
                                                        <TableCell>{c.phone}</TableCell>
                                                        <TableCell>
                                                            {/* ✅ FIXED: formatStatus removes underscores and capitalizes */}
                                                            <Chip
                                                                label={formatStatus(c.status)}
                                                                size="small"
                                                                color={c.status === 'application_shortlisted' ? 'success' : 'default'}
                                                                sx={{ height: 20, fontSize: '0.65rem' }}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                <Typography variant="caption" color="text.secondary">
                                    Displaying {filteredCandidates.length} candidates
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

const Stack = ({ children, spacing }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: spacing }}>
        {children}
    </Box>
);

export default AICalling;
